import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  getOrganizationProfileApi,
  type OrganizationProfile,
} from '@/lib/api'
import { BRAND_MINT, cn } from '@/lib/utils'
import {
  BuildingIcon,
  GlobeIcon,
  HashIcon,
  ImageIcon,
  ShieldCheckIcon,
  PencilIcon,
  MapPinIcon,
  PhoneIcon,
  FileSignatureIcon,
} from 'lucide-react'

/** Color treatment for each verification status. */
const STATUS_COLORS: Record<string, string> = {
  verified: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  active: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  pending: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  suspended: 'bg-destructive/10 text-destructive',
  rejected: 'bg-destructive/10 text-destructive',
}

export const Route = createFileRoute('/dashboard/company-profile')({
  component: () => {
    const routerState = useRouterState()
    const isListRoute = routerState.location.pathname === '/dashboard/company-profile'

    const [profile, setProfile] = useState<OrganizationProfile | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
      if (!isListRoute) return

      let cancelled = false

      async function load() {
        try {
          setLoading(true)
          setError(null)
          const res = await getOrganizationProfileApi()
          if (!cancelled) setProfile(res.organization)
        } catch (err) {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : 'Failed to load company profile')
            toast.error(err instanceof Error ? err.message : 'Failed to load company profile')
          }
        } finally {
          if (!cancelled) setLoading(false)
        }
      }

      load()
      return () => { cancelled = true }
    }, [isListRoute])

    if (!isListRoute) {
      return <Outlet />
    }

    if (loading) {
      return <CompanyProfileSkeleton />
    }

    if (error || !profile) {
      return (
        <div className="mx-auto max-w-2xl">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-8 text-center">
            <p className="text-sm text-destructive">{error || 'Organization not found'}</p>
          </div>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 flex items-start justify-between">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 shrink-0"
              style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
            />
            <div>
              <h1 className="text-2xl font-semibold">
                {profile.org_name}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Registered business details, tax information, and contact info.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild size="sm" variant="outline" className="gap-1.5">
              <Link to="/dashboard/company-profile/signatory">
                <FileSignatureIcon className="size-4" />
                Signatory
              </Link>
            </Button>
            <Button asChild size="sm" className="gap-1.5">
              <Link to="/dashboard/company-profile/edit">
                <PencilIcon className="size-4" />
                Edit
              </Link>
            </Button>
          </div>
        </div>

        {/* Company name card */}
        <div className="mb-6 flex items-center gap-4 rounded-xl border border-border bg-card p-5">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <BuildingIcon className="size-5" />
          </div>
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold">{profile.org_name}</h2>
            <span className={cn(
                'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
                STATUS_COLORS[profile.verified_status] ?? 'bg-muted text-muted-foreground',
              )}>
              <ShieldCheckIcon className="size-3" />
              {profile.verified_status}
            </span>
          </div>
        </div>

        {/* Detail fields */}
        <div className="space-y-4 rounded-xl border border-border bg-card p-6">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Business details
          </h3>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-start gap-3">
              <HashIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Legal entity name</p>
                <p className="truncate text-sm font-medium">{profile.legal_entity_name || '—'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <HashIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">VAT number</p>
                <p className="truncate text-sm font-medium">{profile.vat_number || '—'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <HashIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">D-U-N-S number</p>
                <p className="truncate text-sm font-medium">{profile.duns_number || '—'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <HashIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Registration number</p>
                <p className="truncate text-sm font-medium">{profile.registration_number || '—'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <GlobeIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Country</p>
                <p className="truncate text-sm font-medium">{profile.country_name || '—'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3 sm:col-span-2">
              <ImageIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Logo URL</p>
                <p className="break-all text-sm font-medium">{profile.logo_url || '—'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3 sm:col-span-2">
              <MapPinIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Registered address</p>
                <p className="text-sm font-medium whitespace-pre-line">{profile.registered_address || '—'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3 sm:col-span-2">
              <MapPinIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Operational address</p>
                <p className="text-sm font-medium whitespace-pre-line">{profile.operational_address || '—'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3 sm:col-span-2">
              <PhoneIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Billing email</p>
                <p className="text-sm font-medium">{profile.billing_email || '—'}</p>
              </div>
            </div>

            {profile.phone_numbers && profile.phone_numbers.length > 0 && (
              <div className="flex items-start gap-3 sm:col-span-2">
                <PhoneIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Phone numbers</p>
                  <div className="space-y-1">
                    {profile.phone_numbers.map((pn, i) => (
                      <p key={pn.org_phone_id ?? i} className="text-sm font-medium">
                        {pn.phone_number}
                        {pn.description && (
                          <span className="text-xs text-muted-foreground"> — {pn.description}</span>
                        )}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  },
})

function CompanyProfileSkeleton() {
  return (
    <div className="mx-auto max-w-2xl animate-pulse">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <div className="h-7 w-40 rounded bg-muted" />
          <div className="mt-2 h-4 w-56 rounded bg-muted" />
        </div>
        <div className="h-9 w-20 rounded bg-muted" />
      </div>

      <div className="mb-6 flex items-center gap-4 rounded-xl border border-border p-5">
        <div className="size-12 rounded-lg bg-muted" />
        <div className="space-y-2">
          <div className="h-5 w-48 rounded bg-muted" />
          <div className="h-5 w-20 rounded-full bg-muted" />
        </div>
      </div>

      <div className="space-y-4 rounded-xl border border-border p-6">
        <div className="h-4 w-28 rounded bg-muted" />
        <div className="grid gap-4 sm:grid-cols-2">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="space-y-1.5">
              <div className="h-3 w-16 rounded bg-muted" />
              <div className="h-4 w-36 rounded bg-muted" />
            </div>
          ))}
          <div className="space-y-1.5 sm:col-span-2">
            <div className="h-3 w-16 rounded bg-muted" />
            <div className="h-4 w-48 rounded bg-muted" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <div className="h-3 w-12 rounded bg-muted" />
            <div className="h-4 w-56 rounded bg-muted" />
          </div>
        </div>
      </div>
    </div>
  )
}
